import { SCHEDULED_NOTIFICATION_WINDOW } from '@/resources/constants/orders'
import { DeliveryType, OrderStatus } from '@/resources/enums'
import { NotificationType } from '@/resources/enums/orders'
import { ActiveOrder, Delivery } from '@/resources/types'
import {
  ScheduledNotificationRange,
  ScheduledNotificationType,
} from '@/resources/types/notification'
import { addTimeZone } from '@/resources/utils'
import { MutableRefObject } from 'react'

export const getNotificationType = (hasTurbo: boolean) =>
  hasTurbo ? NotificationType.TURBO : NotificationType.DEFAULT

const isPendingOrder = (status: OrderStatus | string) =>
  status === (OrderStatus.PENDING as string) ||
  status === (OrderStatus.TRANSFERED as string)

const isTurboOrder = (delivery: Delivery) =>
  delivery?.type === DeliveryType.TURBO

const getPendingTurboOrderNumbers = (orders: ActiveOrder[]) => {
  const pendingTurboOrders = orders?.filter(
    ({ status, delivery }) => isPendingOrder(status) && isTurboOrder(delivery)
  )
  return pendingTurboOrders.map(({ number }) => number)
}

const hasNewPendingTurboOrders = (
  turboOrders: string[],
  oldTurboOrders: string[]
) => {
  const newTurboOrders = turboOrders.filter(
    orderNumber => !oldTurboOrders.includes(orderNumber)
  )

  return newTurboOrders.length > 0
}

export const validateTurboOrders = (
  orders: ActiveOrder[],
  prevPendingTurboOrderNumbers: MutableRefObject<string[]>
) => {
  const pendingTurboOrderNumbers = getPendingTurboOrderNumbers(orders)
  const hasNewTurboOrders = hasNewPendingTurboOrders(
    pendingTurboOrderNumbers,
    prevPendingTurboOrderNumbers.current
  )
  prevPendingTurboOrderNumbers.current = pendingTurboOrderNumbers

  return hasNewTurboOrders
}

export const hasPendingOrders = (orders: ActiveOrder[]) => {
  const pendingOrders =
    orders?.filter(({ status }) => isPendingOrder(status)) ?? []

  const hasPending = pendingOrders.length > 0
  const hasTurbo = pendingOrders.some(({ delivery }) => isTurboOrder(delivery))

  return { hasPending, hasTurbo }
}

const getTimeRangeFromStartDate = (
  timeDiff: number
): ScheduledNotificationRange | null => {
  if (timeDiff >= SCHEDULED_NOTIFICATION_WINDOW.WINDOW_1H30M) {
    return ScheduledNotificationRange.ONE_HOUR_THIRTY_MIN_AFTER_START
  }
  if (timeDiff >= SCHEDULED_NOTIFICATION_WINDOW.WINDOW_1H) {
    return ScheduledNotificationRange.ONE_HOUR_AFTER_START
  }
  if (timeDiff >= SCHEDULED_NOTIFICATION_WINDOW.WINDOW_30M) {
    return ScheduledNotificationRange.THIRTY_MIN_AFTER_START
  }
  if (
    timeDiff < 0 &&
    timeDiff >= -SCHEDULED_NOTIFICATION_WINDOW.BEFORE_DELIVERY
  ) {
    return ScheduledNotificationRange.TEN_MIN_BEFORE_START
  }
  return null
}

export const SCHEDULED_NOTIFICATION_KEY = 'SCHEDULED_ORDERS_NOTIFICATION'

const getScheduledNotificationTracking = () => {
  const notificationTrackingJson = localStorage.getItem(
    SCHEDULED_NOTIFICATION_KEY
  )

  let notificationTracking: ScheduledNotificationType = {}
  if (notificationTrackingJson) {
    try {
      notificationTracking = JSON.parse(
        notificationTrackingJson
      ) as ScheduledNotificationType
    } catch {
      // On parsing error, ignore localStorage value and use the new object
    }
  }
  return notificationTracking
}

export const updateScheduledOrdersNotificationTracking = (
  scheduledOrders: ActiveOrder[]
) => {
  const now = Date.now()
  const notificationTracking = getScheduledNotificationTracking()
  const updatedTracking: ScheduledNotificationType = {}
  let shouldNotify = false

  scheduledOrders.forEach(({ number, delivery }) => {
    const startDate = delivery.deliveryDate?.startDate
    if (!startDate) return

    const deliveryStart = addTimeZone(startDate).getTime()
    const timeDiff = now - deliveryStart
    const range = getTimeRangeFromStartDate(timeDiff)

    if (range) {
      updatedTracking[number] = range
      const orderInTracking = notificationTracking[number]
      if (!orderInTracking || (orderInTracking && orderInTracking !== range)) {
        shouldNotify = true
      }
    }
  })

  localStorage.setItem(
    SCHEDULED_NOTIFICATION_KEY,
    JSON.stringify(updatedTracking)
  )

  return shouldNotify
}
